import axios from 'axios';
import {
  notificationPayloadToNum,
  type NotificationPayload,
} from './notificationPayload';
import { shouldShowMapNotificationOnWeb, isValidLinkedSiteId } from './buttonAlertSiteLink';
import { pickButtonAlertId } from './notificationPayload';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';
const CACHE_TTL_MS = 90_000;

export type ButtonRefFromPayload = {
  identification?: string;
  deviceId?: number;
};

export type VerifyButtonSiteLinkResult = {
  linked: boolean;
  siteId?: number;
  deviceId?: number;
  siteName?: string;
  siteAddress?: string;
};

type CacheEntry = {
  at: number;
  result: VerifyButtonSiteLinkResult;
};

const cache = new Map<string, CacheEntry>();

function cacheKey(refs: ButtonRefFromPayload): string {
  return `${refs.identification ?? ''}|${refs.deviceId ?? ''}`;
}

function flattenNotificationRecord(raw: unknown): Record<string, unknown> {
  if (raw == null || typeof raw !== 'object') return {};
  const o = raw as Record<string, unknown>;
  const nested =
    o.data && typeof o.data === 'object'
      ? (o.data as Record<string, unknown>)
      : o.payload && typeof o.payload === 'object'
        ? (o.payload as Record<string, unknown>)
        : null;
  const merged = nested ? { ...o, ...nested } : { ...o };
  const inner =
    merged.data && typeof merged.data === 'object' ? (merged.data as Record<string, unknown>) : null;
  return inner ? { ...merged, ...inner } : merged;
}

/** id_button / identification_button / deviceId del socket o payload anidado. */
export function pickButtonRefsFromPayload(src: Record<string, unknown>): ButtonRefFromPayload {
  const identificationKeys = [
    'id_button',
    'identification_button',
    'identificationButton',
    'buttonId',
    'button_id',
  ] as const;
  let identification: string | undefined;
  for (const k of identificationKeys) {
    const v = src[k];
    if (v != null && String(v).trim() !== '') {
      identification = String(v).trim();
      break;
    }
  }

  let deviceId: number | undefined;
  for (const k of ['deviceId', 'device_id'] as const) {
    const v = notificationPayloadToNum(src[k]);
    if (v != null && v > 0) {
      deviceId = v;
      break;
    }
  }

  return { identification, deviceId };
}

export function pickButtonRefsFromNotificationRaw(raw: unknown): ButtonRefFromPayload {
  return pickButtonRefsFromPayload(flattenNotificationRecord(raw));
}

export function hasButtonRefInPayload(refs: ButtonRefFromPayload): boolean {
  return !!(refs.identification || (refs.deviceId != null && refs.deviceId > 0));
}

export async function verifyButtonSiteLink(
  token: string,
  refs: ButtonRefFromPayload
): Promise<VerifyButtonSiteLinkResult> {
  if (!hasButtonRefInPayload(refs)) {
    return { linked: false };
  }

  const key = cacheKey(refs);
  const cached = cache.get(key);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.result;
  }

  try {
    const params: Record<string, string | number> = {};
    if (refs.identification) params.id_button = refs.identification;
    if (refs.deviceId != null) params.deviceId = refs.deviceId;

    const res = await axios.get<{ success?: boolean; data?: VerifyButtonSiteLinkResult }>(
      `${API_URL}/device-buttons/verify-site-link`,
      {
        headers: { Authorization: `Bearer ${token}` },
        params,
      }
    );
    const data = res.data?.data;
    const result: VerifyButtonSiteLinkResult = {
      linked: data?.linked === true,
      siteId: data?.siteId,
      deviceId: data?.deviceId,
      siteName: data?.siteName,
      siteAddress: data?.siteAddress,
    };
    cache.set(key, { at: Date.now(), result });
    return result;
  } catch {
    const result: VerifyButtonSiteLinkResult = { linked: false };
    cache.set(key, { at: Date.now(), result });
    return result;
  }
}

async function verifyViaButtonAlertId(
  token: string,
  buttonAlertId: number
): Promise<VerifyButtonSiteLinkResult> {
  const key = `alert:${buttonAlertId}`;
  const cached = cache.get(key);
  if (cached && Date.now() - cached.at < CACHE_TTL_MS) {
    return cached.result;
  }
  try {
    const res = await axios.get<{ success?: boolean; data?: { siteId?: number | null; deviceId?: number } }>(
      `${API_URL}/button-alerts/${buttonAlertId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const row = res.data?.data;
    if (!row || !isValidLinkedSiteId(row.siteId)) {
      const result: VerifyButtonSiteLinkResult = { linked: false };
      cache.set(key, { at: Date.now(), result });
      return result;
    }
    const result: VerifyButtonSiteLinkResult = {
      linked: true,
      siteId: Number(row.siteId),
      deviceId: row.deviceId,
    };
    cache.set(key, { at: Date.now(), result });
    return result;
  } catch {
    const result: VerifyButtonSiteLinkResult = { linked: false };
    cache.set(key, { at: Date.now(), result });
    return result;
  }
}

/**
 * Si el socket trae id de botón, consulta BD antes de mostrar UI.
 * Alertas de mapa EU sin botón verificable no se muestran (ignora siteId del middleware).
 */
export async function resolveMapNotificationWebVisibility(
  payload: NotificationPayload,
  raw: unknown,
  token: string | null
): Promise<{ show: boolean; verifiedSiteId?: number; verifiedSiteName?: string; verifiedSiteAddress?: string }> {
  const isWorkerNotification =
    payload.mapNotificationId != null && Number(payload.mapNotificationId) > 0;
  if (isWorkerNotification) {
    return { show: shouldShowMapNotificationOnWeb(payload) };
  }

  if (payload.scope !== 'map') {
    return { show: true };
  }

  if (!token) {
    return { show: false };
  }

  const refs = pickButtonRefsFromNotificationRaw(raw);
  if (hasButtonRefInPayload(refs)) {
    const verified = await verifyButtonSiteLink(token, refs);
    if (!verified.linked) return { show: false };
    return {
      show: true,
      verifiedSiteId: verified.siteId,
      verifiedSiteName: verified.siteName,
      verifiedSiteAddress: verified.siteAddress,
    };
  }

  const flat = flattenNotificationRecord(raw);
  const buttonAlertId =
    pickButtonAlertId(flat) ??
    (payload.buttonAlertId != null && payload.buttonAlertId > 0 ? payload.buttonAlertId : undefined);
  if (buttonAlertId != null) {
    const verified = await verifyViaButtonAlertId(token, buttonAlertId);
    if (!verified.linked) return { show: false };
    return {
      show: true,
      verifiedSiteId: verified.siteId,
    };
  }

  return { show: false };
}

export function applyVerifiedSiteToPayload<T extends NotificationPayload>(
  payload: T,
  verified: { verifiedSiteId?: number; verifiedSiteName?: string; verifiedSiteAddress?: string }
): T {
  if (verified.verifiedSiteId == null) return payload;
  return {
    ...payload,
    siteId: verified.verifiedSiteId,
    ...(verified.verifiedSiteName ? { siteName: verified.verifiedSiteName } : {}),
    ...(verified.verifiedSiteAddress ? { siteAddress: verified.verifiedSiteAddress } : {}),
  };
}
